import clsx from 'clsx'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components-latest/primitives/Controls/Select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger
} from '@/components-latest/primitives/DropdownMenu'
import { ChevronDown, Globus } from '@/components-latest/primitives/Icons'
import Text, { textVariants } from '@/components-latest/primitives/Text'
import { APP_SUPPORTED_LANGUAGES } from '@/config/i18n'

import Styles from './LanguageSelect.module.scss'

type Props = {
  className?: string
}

const LanguageSelectSimple = ({ className }: Props) => {
  const { i18n } = useTranslation()
  const { lmd_store_webapp_registration_page } = useFlags()

  const currentLanguage = i18n.resolvedLanguage

  const onLanguageChange = (language: string) => {
    i18n.changeLanguage(
      APP_SUPPORTED_LANGUAGES.includes(language) ? language : 'en'
    )
  }

  const LANGUAGES = useMemo(() => {
    return [
      { value: 'en', label: 'English' },
      { value: 'fr', label: 'Français' },
      ...(lmd_store_webapp_registration_page
        ? [{ value: 'ar', label: 'عربي' }]
        : [])
    ]
  }, [lmd_store_webapp_registration_page])

  return (
    <Select onValueChange={onLanguageChange} value={currentLanguage}>
      <SelectTrigger
        arrowVariant="solidArrow"
        className={clsx(
          Styles.selectTrigger,
          textVariants({ variant: 'nextBody' }),
          className
        )}>
        <SelectValue asChild>
          <Text variant="nextBody">
            {
              LANGUAGES.find(language => language.value === currentLanguage)
                ?.label
            }
          </Text>
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map(language => (
          <SelectItem key={language.value} value={language.value}>
            {language.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface ILanguageSelectProps {}

const LanguageSelect: React.FC<ILanguageSelectProps> = () => {
  const { i18n } = useTranslation()
  const { lmd_store_webapp_registration_page } = useFlags()

  const [isOpen, setIsOpen] = useState(false)

  const currentLanguage = i18n.resolvedLanguage

  const onLanguageChange = (language: string) => {
    i18n.changeLanguage(
      APP_SUPPORTED_LANGUAGES.includes(language) ? language : 'en'
    )
  }

  const LANGUAGES = useMemo(() => {
    return [
      { value: 'en', label: 'English' },
      { value: 'fr', label: 'Français' },
      ...(lmd_store_webapp_registration_page
        ? [{ value: 'ar', label: 'عربي' }]
        : [])
    ]
  }, [lmd_store_webapp_registration_page])

  return (
    <div>
      <DropdownMenu open={isOpen} onOpenChange={open => setIsOpen(open)}>
        <DropdownMenuTrigger asChild>
          <button className={Styles.languageButton}>
            <Globus width={16} height={16} />
            <Text variant="nextBody">
              {
                LANGUAGES.find(language => language.value === currentLanguage)
                  ?.label
              }
            </Text>
            <ChevronDown
              width={16}
              height={16}
              className={clsx(Styles.chevron, { [Styles.rotate180]: isOpen })}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={Styles.content}>
          <DropdownMenuRadioGroup onValueChange={onLanguageChange}>
            {LANGUAGES.map(language => (
              <DropdownMenuRadioItem
                key={language.value}
                value={language.value}
                asChild>
                <DropdownMenuItem>{language.label}</DropdownMenuItem>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export { LanguageSelectSimple }
export default LanguageSelect
